import { Logger } from "./logger";
import { ApiClientFactory } from "./api.client-factory";
import {
  ListingFileType,
  DeleteListingDocumentCommand,
  DeleteListingImageCommand,
  DeleteSpecificListingFileCommand,
  ListingDocumentDto,
  EditListingPriceCommand,
  DeleteListingCommand,
  ListingCategoryDto,
  ListingImageDto,
  PaginatedResultOfListingLookupDto,
  RegisterListingVisitCommand,
  ListingPriceChangeDto,
  CreateOrEditListingSellerCommand,
  SellerDto,
  ICreateOrEditListingSellerCommand,
  IAddressInput,
  ListingContractDocumentDto,
  CreateOrEditListingCategoryCommand,
  FileParameter,
  UpdateListingCompletedStepCommand,
  IInviteSellerCommand,
  InviteSellerCommand,
  EditListingImageCommand,
  DeclineInviteSellerCommand,
  LanguageType,
  ConfirmInviteSellerCommand,
  ReorderListingCategoryImagesCommand,
  ISellerDto,
  PersonaVerificationDto,
  AddressInput,
} from "common/middleware/api.services";
import { mutateUnit, mutatePropertyImages } from "./hooks";
import { mutateImageCategories, mutateSellers } from "./hooks/properties.hooks";
import { ApiError, ApiValidationError, callApiAndHandleApiException, callApiAndHandleExpectedApiExceptions } from "./api.helpers";
import { getLoggedInUser } from "./api.auth";
import { ListingStep } from "./types.properties";
import store from "./store";

import { getUser } from "./api";
import { getLocale } from "./localization";

export const getPropertyDocuments = async (
  key: string,
  propertyId: number,
  draft?: boolean,
  filterOnSupplementaryDocumentName?: string,
  getPrivate?: boolean,
): Promise<ListingDocumentDto[]> => {
  Logger.debug("Getting property documents", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: getPrivate ?? false,
  });

  return await client.getListingDocuments(
    propertyId,
    draft,
    filterOnSupplementaryDocumentName,
  );
}

export const getPropertyImages = async (
  key: string,
  propertyId: number,
  draft?: boolean
): Promise<ListingImageDto[]> => {
  Logger.debug("Getting property images", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: false,
  });

  try {
    return await client.getListingImages(propertyId, draft);
  } catch (error) {
    if (error?.response?.status === 404 && draft) {
      return null;
    }

    throw error;
  }
}

export const deletePropertyImage = async (
  propertyId: number,
  imageMediaId: string,
): Promise<string> => {
  Logger.debug(
    "Removing property image",
    `propertyId: ${propertyId}`,
    `imageMediaId, ${imageMediaId}`
  );

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.deleteListingImages2(
    propertyId,
    imageMediaId,
    new DeleteListingImageCommand(),
  );

  Logger.debug("Successfully removed property image");

  await mutatePropertyImages(propertyId);

  return res;
}

export const deletePropertyDocument = async (
  propertyId: number,
  documentId: string,
): Promise<string> => {
  Logger.debug(
    "Removing property document",
    `propertyId: ${propertyId}`,
    `documentId, ${documentId}`
  );

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.deleteListingDocuments(
    propertyId,
    documentId,
    new DeleteListingDocumentCommand(),
  );

  Logger.debug("Successfully removed property document");

  return res;
}

export const deletePropertyFile = async (
  propertyId: number,
  propertyFileType: ListingFileType,
): Promise<string> => {
  Logger.debug(
    "Deleting property file",
    `propertyId: ${propertyId}`,
    `propertyFileType: ${propertyFileType}`,
  );

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.deleteListingImages(
    propertyId,
    new DeleteSpecificListingFileCommand({ listingFileType: propertyFileType }),
  );

  Logger.debug("Successfully deleted property file");

  await mutateUnit(propertyId);

  return res;
}

export const editPrice = async (
  propertyId: number,
  newPrice: number,
) => {
  Logger.debug("Editing property price", { propertyId, newPrice });

  const client = await ApiClientFactory.getPropertiesClient();

  await client.editPrice(
    propertyId,
    new EditListingPriceCommand({ price: newPrice })
  );

  Logger.debug("Successfully edited price");
}

export const deleteProperty = async (propertyId: number): Promise<{
  isSuccessful?: boolean,
  isFailDueToExistingOffer?: boolean,
}> => {
  Logger.debug("Deleting property", propertyId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await callApiAndHandleExpectedApiExceptions(async () => (
    await client.delete(propertyId, new DeleteListingCommand())
  ), ["can_not_delete_property_Exception"]);

  if (res instanceof ApiError) {
    Logger.debug("Failed to delete property", propertyId);

    if (res.errorCode === "can_not_delete_property_Exception") {
      return {
        isFailDueToExistingOffer: true,
      }
    }
  }

  Logger.debug("Successfully deleted property", propertyId);

  return {
    isSuccessful: true,
  }
}

export const uploadImagesToCategory = async (
  propertyId: number,
  categoryId: string,
  formFiles: FileParameter[]
): Promise<ListingImageDto[]> => {
  Logger.debug("Adding images to", categoryId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.uploadImagesToCategory(
    propertyId,
    categoryId,
    formFiles
  );

  Logger.debug("Successfully added images to", categoryId);

  await mutatePropertyImages(propertyId);

  return res;
}

export const editImageCaption = async (
  propertyId: number,
  imageId: string,
  caption: string
): Promise<ListingImageDto> => {
  Logger.debug("Editing image", imageId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.editImage(
    propertyId,
    imageId,
    new EditListingImageCommand({ caption })
  );

  Logger.debug("Successfully edited image for", propertyId);

  await mutatePropertyImages(propertyId);

  return res;
}

export const editImageSortOrder = async (
  propertyId: number,
  categoryId: string,
  propertyImageIds: string[]
): Promise<ListingImageDto[]> => {
  Logger.debug("Editing sort order for", categoryId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.reorderImagesInCategory(
    propertyId,
    categoryId,
    new ReorderListingCategoryImagesCommand({ listingImageIds: propertyImageIds }),
  );

  Logger.debug("Successfully updated image sort order for", categoryId);

  await mutatePropertyImages(propertyId);

  return res;
}

export const createImageCategory = async (
  propertyId: number,
  name: string
): Promise<ListingCategoryDto> => {
  Logger.debug("Creating image category for property", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: true
  });

  const res = await client.createCategory(
    propertyId,
    new CreateOrEditListingCategoryCommand({ name }),
  );

  await mutateImageCategories(propertyId);

  return res;
}

export const editImageCategory = async (
  propertyId: number,
  categoryId: string,
  name: string
): Promise<ListingCategoryDto> => {
  Logger.debug("Editing image category for property", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: true
  });

  const res = await client.editCategory(
    propertyId,
    categoryId,
    new CreateOrEditListingCategoryCommand({ name }),
  );

  await mutateImageCategories(propertyId);

  return res;
}

export const deleteImageCategory = async (
  propertyId: number,
  categoryId: string,
) => {
  Logger.debug("Deleting image category", { propertyId, categoryId });

  const client = await ApiClientFactory.getPropertiesClient({ addAccessToken: true });

  const res = await client.deleteCategory(propertyId, categoryId);

  await mutateImageCategories(propertyId);

  return res;
}

export const getImageCategories = async (
  key: string,
  propertyId: number,
  draft?: boolean
): Promise<ListingCategoryDto[]> => {
  Logger.debug("Getting image categories for property", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({ addAccessToken: false });

  return await client.getCategories(propertyId, draft);
}

export const registerPropertyVisit = async (
  propertyId: string | number,
) => {
  const visitedProperties = JSON.parse(sessionStorage.getItem("visitedProperties")) ?? [];

  if (
    !propertyId
    || visitedProperties.includes(Number(propertyId))
  ) {
    return;
  }

  Logger.debug("Registering visit for property", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: false,
  });

  await client.registerVisits(
    Number(propertyId),
    new RegisterListingVisitCommand(),
  );

  sessionStorage.setItem(
    "visitedProperties",
    JSON.stringify([...visitedProperties, Number(propertyId)])
  );

  Logger.debug("Successfully registered visit", propertyId);
}

export const getPropertyPriceHistory = async (
  key: string,
  propertyId: number,
): Promise<ListingPriceChangeDto[]> => {
  Logger.debug("Getting property price history", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({
    addAccessToken: false,
  });

  const res = await client.getPriceChanges(Number(propertyId));

  Logger.debug("Successfully retrieved price history");

  res.sort((a, b) => b.priceChangeDate.getTime() - a.priceChangeDate.getTime());

  return res;
}

export const createSeller = async (
  propertyId: number,
  data: Omit<ICreateOrEditListingSellerCommand, "residentAddress">
    & { residentAddress?: IAddressInput },
): Promise<SellerDto> => {
  Logger.debug("Creating seller", { propertyId });

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.createSeller(
    propertyId,
    new CreateOrEditListingSellerCommand({
      ...data,
      residentAddress: new AddressInput(data.residentAddress)
    })
  );

  Logger.debug("Successfully created seller", { sellerId: res.id });

  return res;
}

export const getSeller = async (
  key: string,
  propertyId: number,
  sellerId: string,
  getDraft: boolean,
): Promise<SellerDto> => {
  Logger.debug("Getting seller", sellerId);

  const client = await ApiClientFactory.getPropertiesClient();

  return await client.getSeller(propertyId, sellerId, getDraft);
}

export function getSellers(
  key: string,
  propertyId: number,
  getIdVerificationStatus?: false,
  getDraft?: boolean,
): Promise<ISellerDto[]>;
export function getSellers(
  key: string,
  propertyId: number,
  getIdVerificationStatus: true,
  getDraft?: boolean,
): Promise<(ISellerDto & { idVerification: PersonaVerificationDto })[]>;
export async function getSellers(
  key: string,
  propertyId: number,
  getIdVerificationStatus?: boolean,
  getDraft?: boolean,
): Promise<ISellerDto[] | (ISellerDto & { idVerification: PersonaVerificationDto })[]> {
  Logger.debug("Getting sellers", { propertyId });

  const client = await ApiClientFactory.getPropertiesClient({ addAccessToken: false });

  try {
    const sellers = await client.getSellers(propertyId, getDraft);

    if (getIdVerificationStatus) {
      const sellersWithIdStatus = await Promise.all(
        sellers.map(async (s) => {
          let idVerification: PersonaVerificationDto;

          if (s.linkedUserId) {
            const user = await getUser(undefined, s.linkedUserId);
            idVerification = user.lastPersonaIdentityVerification;
          }

          return {
            ...s,
            idVerification,
          }
        })
      );

      return sellersWithIdStatus;
    }

    return sellers;
  } catch (error) {
    if (error?.response?.status === 404 && getDraft) {
      return null;
    }

    throw error;
  }
}

export function getLoggedInSeller(
  key: string,
  propertyId: number,
  getIdVerificationStatus?: false,
  getDraft?: boolean,
): Promise<ISellerDto>;
export function getLoggedInSeller(
  key: string,
  propertyId: number,
  getIdVerificationStatus: true,
  getDraft?: boolean,
): Promise<ISellerDto & { idVerification: PersonaVerificationDto }>;
export async function getLoggedInSeller(
  key: string,
  propertyId: number,
  getIdVerificationStatus?: boolean,
  getDraft?: boolean,
): Promise<ISellerDto | (ISellerDto & { idVerification: PersonaVerificationDto })> {
  Logger.debug("Getting logged in user's seller profile", { propertyId });

  const [user, sellers] = await Promise.all([
    getIdVerificationStatus ? getLoggedInUser() : undefined,
    getSellers(undefined, propertyId, undefined, getDraft),
  ]);

  const seller = sellers?.find(s => s.linkedUserId === store.userId) || null;

  return getIdVerificationStatus
    ? {
        ...seller,
        idVerification: user.personaVerification,
      }
    : seller
}

export const editSeller = async (
  propertyId: number,
  sellerId: string,
  data: Omit<ICreateOrEditListingSellerCommand, "residentAddress">
    & { residentAddress?: IAddressInput },
): Promise<SellerDto> => {
  Logger.debug("Editing seller", sellerId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.editSeller(
    propertyId,
    sellerId,
    new CreateOrEditListingSellerCommand({
      ...data,
      residentAddress: new AddressInput({
        street: data.residentAddress.street,
        unitNumber: data.residentAddress.unitNumber,
        city: data.residentAddress.city,
        province: data.residentAddress.province,
        postalCode: data.residentAddress.postalCode,
        country: data.residentAddress.country,
      }),
    })
  );

  Logger.debug("Successfully edited seller", sellerId);

  return res;
}

export const getContractDocument = async (
  key: string,
  propertyId: number,
  locale: string,
): Promise<ListingContractDocumentDto> => {
  Logger.debug("Getting contract document", { propertyId });

  const client = await ApiClientFactory.getPropertiesClient();

  return await client.getListingContractDocument(propertyId, locale);
}

export const updateListingCompletedStep = async (
  propertyId: number,
  completedStep: ListingStep,
  isDraft: boolean = false,
) => {
  Logger.debug("Updating last completed step", { propertyId, completedStep, isDraft });

  const client = await ApiClientFactory.getPropertiesClient();

  await client.updateListingCompletedStep(
    propertyId,
    new UpdateListingCompletedStepCommand({
      lastCompletedStep: completedStep.toString(),
    })
  );

  Logger.debug("Successfully updated last completed step");

  await mutateUnit(propertyId, isDraft);
}

export const inviteSeller = async (
  propertyId: number,
  seller: IInviteSellerCommand,
): Promise<SellerDto> => {
  Logger.debug("Inviting seller");

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await client.inviteSeller(
    propertyId,
    new InviteSellerCommand(seller),
  );

  await mutateSellers(propertyId);

  return res;
}

export const resendInviteSellerEmail = async (
  propertyId: number,
  sellerId: string,
) => {
  Logger.debug("Resending seller invite email");

  const client = await ApiClientFactory.getPropertiesClient();

  await client.reSendSellerEmail(propertyId, sellerId);
}

export const sellerDeclineListing = async (
  propertyId: number,
  sellerId: string,
  preferredLanguage: LanguageType,
): Promise<{
  seller?: SellerDto,
  isFailDueToInvalidStateChange?: boolean,
}> => {
  Logger.debug("Declining listing", propertyId);

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await callApiAndHandleExpectedApiExceptions(async () => (
    await client.declineInviteSeller(
      propertyId,
      sellerId,
      new DeclineInviteSellerCommand({ language: preferredLanguage }),
    )
  ), ["property_invalid_state_change"]);

  if (res instanceof ApiError) {
    if (res.errorCode === "property_invalid_state_change") {
      return { isFailDueToInvalidStateChange: true }
    }

    throw res;
  }

  if (res instanceof ApiValidationError) {
    throw res;
  }

  return { seller: res };
}

export const sellerConfirmListing = async (
  propertyId: number,
  sellerId: string,
): Promise<{
  seller?: SellerDto,
  isFailDueToInvalidStateChange?: boolean,
  isFailDueToAlreadyConfirmed?: boolean,
  isFailDueToUserCannotConfirmSellerInvite?: boolean,
}> => {
  Logger.debug("Confirming listing", { propertyId, sellerId });

  const client = await ApiClientFactory.getPropertiesClient();

  const res = await callApiAndHandleExpectedApiExceptions(async () => (
    await client.confirmInviteSeller(
      propertyId,
      sellerId,
      new ConfirmInviteSellerCommand(),
    )
  ), [
    "property_invalid_state_change",
    "property_seller_already_confirmed",
    "user_cannot_confirm_seller_invite",
  ]);

  if (res instanceof ApiError) {
    if (res.errorCode === "property_invalid_state_change") {
      return { isFailDueToInvalidStateChange: true }
    }

    if (res.errorCode === "property_seller_already_confirmed") {
      return { isFailDueToAlreadyConfirmed: true }
    }

    if (res.errorCode === "user_cannot_confirm_seller_invite") {
      return { isFailDueToUserCannotConfirmSellerInvite: true }
    }

    throw res;
  }

  if (res instanceof ApiValidationError) {
    throw res;
  }

  return { seller: res };
}

export const getReviewGroupSections = async (
  key: string,
  propertyId: number,
) => {
  Logger.debug("Getting review group sections", propertyId);

  const client = await ApiClientFactory.getPropertiesClient({ addAccessToken: true });

  return await client.getListingReviewGroupSections(propertyId);
}

export const getDirectoryListings = async (
  _key: string,
  page: number,
  pageSize: number,
  searchString: string | undefined,
  sortOrder: string | undefined,
  ascSortDirection: boolean,
  minLatitude?: number | null,
  maxLatitude?: number | null,
  minLongitude?: number | null,
  maxLongitude?: number | null,
): Promise<PaginatedResultOfListingLookupDto> => {
  const client = await ApiClientFactory.getPropertiesClient({ addAccessToken: false });

  return callApiAndHandleApiException(
    async () =>
      await client.getPublicPage(
        getLocale(),
        minLatitude ?? null,
        maxLatitude ?? null,
        minLongitude ?? null,
        maxLongitude ?? null,
        sortOrder ?? null,
        searchString ?? null,
        page,
        ascSortDirection,
        pageSize,
        false,
      )
  );
};
